import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import AdminPageTabs from '../shared/adminPageTabs.component';
import { TabTypes } from '../shared/constants';

import PeriodDropdown from '../../shared/periodDropdown.component';
import { ReduxAlert } from '../../shared/alert.component';
import NavigationPrompt, { IMPORTED_DATA_PENDING_MESSAGE } from '../../shared/navigationPrompt.component';

import SecondaryMenu from '../../menu/secondaryMenu.component';

import { modalDataSelector } from '../../shared/modals/modals.selectors';

import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../userData/store/selectors';

import {
  periodOrCurrentSelector,
  currentPeriodSelector,
  environmentSuffixSelector,
} from '../../shared/store/selectors';

import getSelectors from '../shared/store/selectorsFactory';
import { adminPageNames, adminPageTitles } from '../shared/adminPageNames';

import TrialBalanceThresholdEditModal from './editTrialBalanceThresholdModal.component';
import TrialBalanceThresholdAdminColumnRenderer from './trialBalanceThresholdColumnRenderer.component';
import columnDefs from '../../shared/columnDefinitions/trialBalanceThreshold';
import ConnectionDropdown from './connectionDropdown.component';
import HamburgerDropdown from '../shared/hamburgerDropdown.component';
import config from '../../config';

import { connectionNameSelector } from './store/selectors';

import { withPermissions, permissions } from '../../shared/authorization';

import { PeriodPropTypes } from '../../shared/propTypes/period';
import { TrialBalanceThresholdPropTypes } from '../../shared/propTypes/TrialBalanceThreshold';
import {
  sharedActions,
} from './store/actions';

import downloadTrialBalanceThresholdXlsx from './downloadTrialBalanceThresholdXlsx';

const pageTitle = adminPageTitles.TRIAL_BALANCE_THRESHOLD;

const TrialBalanceThresholdPage = ({
  period,
  currentPeriod,
  connectionName,
  canModifyInSelectedPeriod: canModify,
  fetchEntities,
  isFetchingEntities,
  entities,
  editModalData,
  hideModalForAddEdit,
  saveEntity,
  environmentSuffix,

  uploadEntitiesFromFile,

  shouldDisplayZero,
  hideDecimals,
  negativeMinus,

  uploadedEntities,
  uploadingEntities,
  importEntities,
  importingEntities,
  clearUploadedEntities,
  areImportsPending,
}) => {
  const [activeTab, changeTab] = useState(TabTypes.VIEW);

  useEffect(() => {
    fetchEntities({ periodName: period.name, connectionName });
    return () => clearUploadedEntities();
  }, [fetchEntities, period, connectionName, importingEntities, clearUploadedEntities]);

  const fileToImportChange = useCallback(event => {
    uploadEntitiesFromFile(
      { file: event.target.files[0] },
      { periodName: currentPeriod.name, connectionName },
      []
    );
  }, [uploadEntitiesFromFile, currentPeriod, connectionName]);

  const fileToImportSelect = useCallback(hideDropDown => () => {
    changeTab(TabTypes.IMPORT);
    hideDropDown();
  }, []);

  const selectFileToUpload = useCallback(file => {
    uploadEntitiesFromFile(
      { file },
      { periodName: currentPeriod.name, connectionName },
      []
    );
  }, [uploadEntitiesFromFile, currentPeriod, connectionName]);

  const importEntityHandler = useCallback(filteredEntities => {
    importEntities(
      filteredEntities,
      {
        periodName: currentPeriod.name,
        connectionName,
      }).then(() => changeTab(TabTypes.VIEW));
  }, [importEntities, currentPeriod, connectionName]);


  const exportToExcelHandler = useCallback(callback => {
    downloadTrialBalanceThresholdXlsx({
      entities,
      periodName: period.name,
      connectionName,
      environmentSuffix,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      columnDefs,
    });
    callback();
  }, [entities, period, connectionName, environmentSuffix, shouldDisplayZero, hideDecimals, negativeMinus]);

  const templatePath = config.templates.TRIAL_BALANCE_THRESHOLD;
  const templateFileName = `TLS_Trial_Balance_Threshold_${period.name}_${connectionName || ''}${environmentSuffix}.xlsx`;

  return (
    <div className="admin-import-page">
      <NavigationPrompt
        show={areImportsPending}
        message={<span>{IMPORTED_DATA_PENDING_MESSAGE}</span>}
      />
      <SecondaryMenu>
        <PeriodDropdown/>
        <ConnectionDropdown/>
        <div className="hamburger-dropdown ml-auto text-right">
          <HamburgerDropdown
            canModify={canModify}
            fileToImportChange={fileToImportChange}
            fileToImportSelect={fileToImportSelect}
            templateFileName={templateFileName}
            templatePath={templatePath}
            exportToExcel={exportToExcelHandler}
            disableExporting={!connectionName}
            disableImporting={!connectionName}
          />
        </div>
      </SecondaryMenu>
      <div className="page">
        <div className="container-fluid">
          <ReduxAlert/>
          <div className="row no-gutters page-content-container">
            <div className="col">
              <div className="d-flex flex-row justify-content-between">
                <h5>{pluralize.plural(pageTitle)}</h5>
              </div>
            </div>
          </div>
          <div className="row no-gutters page-content-container">
            <div className="col">
              <AdminPageTabs
                canModify={canModify}
                pageTitle={pageTitle}
                activeTab={activeTab}
                changeTab={changeTab}
                columnDefs={columnDefs}
                isFetchingEntities={isFetchingEntities}
                entities={entities}
                adminColumnRenderer={TrialBalanceThresholdAdminColumnRenderer}
                comparatorConfig={{
                  idField: 'functionalCurrency',
                }}
                uploadedEntities={uploadedEntities}
                uploadingEntities={uploadingEntities}
                selectFileToUpload={selectFileToUpload}
                importEntities={importEntityHandler}
                templateFileName={templateFileName}
                templatePath={templatePath}
              />
            </div>
          </div>
        </div>
      </div>
      <TrialBalanceThresholdEditModal
        isVisible={editModalData.show}
        connectionName={connectionName}
        entityToEdit={editModalData.data}
        hideModal={hideModalForAddEdit}
        saveEntity={saveEntity}
        period={period}
        openForEdit
      />
    </div>
  );
};

TrialBalanceThresholdPage.propTypes = {
  period: PeriodPropTypes.isRequired,
  connectionName: PropTypes.string,
  currentPeriod: PeriodPropTypes.isRequired,
  canModifyInSelectedPeriod: PropTypes.bool.isRequired,
  fetchEntities: PropTypes.func.isRequired,
  isFetchingEntities: PropTypes.bool.isRequired,
  entities: PropTypes.arrayOf(TrialBalanceThresholdPropTypes).isRequired,
  editModalData: TrialBalanceThresholdPropTypes,
  hideModalForAddEdit: PropTypes.func.isRequired,
  saveEntity: PropTypes.func.isRequired,
  environmentSuffix: PropTypes.string.isRequired,
  shouldDisplayZero: PropTypes.bool.isRequired,
  hideDecimals: PropTypes.bool.isRequired,
  negativeMinus: PropTypes.bool.isRequired,
  uploadEntitiesFromFile: PropTypes.func,
  importEntities: PropTypes.func,
  importingEntities: PropTypes.bool,
  clearUploadedEntities: PropTypes.func,
  uploadedEntities: PropTypes.arrayOf(TrialBalanceThresholdPropTypes).isRequired,
  uploadingEntities: PropTypes.bool.isRequired,
  areImportsPending: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, props) => {
  const generatedSelectors = getSelectors(adminPageNames.TRIAL_BALANCE_THRESHOLD);

  const {
    fetchingEntitiesSelector = generatedSelectors.fetchingEntitiesSelector,
    entitiesSelector = generatedSelectors.entitiesSelector,
    uploadingEntitiesSelector = generatedSelectors.uploadingEntitiesSelector,
    importingEntitiesSelector = generatedSelectors.importingEntitiesSelector,
    uploadedEntitiesSelector = generatedSelectors.uploadedEntitiesSelector,
    areImportsPendingSelector = generatedSelectors.areImportsPendingSelector,
  } = props;

  return {
    editModalData: modalDataSelector(state, `${adminPageNames.TRIAL_BALANCE_THRESHOLD}/addEdit`),

    period: periodOrCurrentSelector(state),
    connectionName: connectionNameSelector(state),
    currentPeriod: currentPeriodSelector(state),

    environmentSuffix: environmentSuffixSelector(state),
    shouldDisplayZero: displayZeroSelector(state),
    hideDecimals: hideDecimalsSelector(state),
    negativeMinus: negativeMinusSelector(state),

    isFetchingEntities: fetchingEntitiesSelector(state),
    entities: entitiesSelector(state),

    uploadingEntities: uploadingEntitiesSelector(state),
    importingEntities: importingEntitiesSelector(state),
    uploadedEntities: uploadedEntitiesSelector(state),
    areImportsPending: areImportsPendingSelector(state),
  };
};

const mapDispatchToProps = dispatch => bindActionCreators(
  {
    hideModalForAddEdit: sharedActions.hideModalForAddEdit,
    fetchEntities: sharedActions.fetchEntities,
    saveEntity: sharedActions.saveEntity,

    uploadEntitiesFromFile: sharedActions.uploadEntitiesFromFile,
    importEntities: sharedActions.importEntities,
    clearUploadedEntities: sharedActions.clearUploadedEntities,
  }
  , dispatch);

export default withPermissions(permissions.TRIAL_BALANCE_THRESHOLD)(
  connect(mapStateToProps, mapDispatchToProps)(TrialBalanceThresholdPage)
);
